import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { PaymentFlowContext } from "src/main/PointOfSale/Checkout/PaymentModal/index"
import { POSContext } from "src/main/PointOfSale/Layout"
import {
  GUEST_CONTACT,
  GUEST_DISPLAY_NAME,
} from "src/main/PointOfSale/constants"

import ContactBoatPicker from "src/components/ContactBoatPicker"
import useContactBoatPicker, {
  PANEL_NONE,
} from "src/components/ContactBoatPicker/useContactBoatPicker"

const EXTRA_FIELDS = ["payment_methods", "dockwa_plus"]

// Shape the contact / contact_boat defaults so the inline-create
// inputs are controlled from first render. Empty strings (not
// undefined) keep React from logging "uncontrolled → controlled" on
// the first keystroke.
const EMPTY_CONTACT = { name: "", email: "", phone: "" }
const EMPTY_CONTACT_BOAT = {
  name: "",
  boat_type: "",
  length_overall: 0,
  beam: 0,
}

// Same pattern as TransientReservationWizard/ContactStep,
// ShortTermWaitlistForm, and Contracts/ContactAndBoat: the picker is
// self-contained; submission calls resolveSelection to either return
// existing ids, create new records, or surface a Guest selection — then
// the parent flow commits the result (here, into POSContext via
// setContact/setBoat).
const PickerInner = ({
  enableGuest,
  submitRef,
  onPanelChange,
  setContact,
  setBoat,
}) => {
  const form = useFormContext()
  const picker = useContactBoatPicker({
    requireBoat: false,
    enableGuest,
    allowBoatFirst: true,
    emptyContact: EMPTY_CONTACT,
    emptyContactBoat: EMPTY_CONTACT_BOAT,
    extraFields: EXTRA_FIELDS,
  })

  const watchedContactId = form.watch("contact_id")
  const watchedIsGuest = form.watch("is_guest")

  // The modal footer reads this to swap "Skip" → "Continue" once the
  // user has engaged with the picker in any way (open panel, picked an
  // existing customer, or chose Guest).
  useEffect(() => {
    const engaged =
      picker.panel !== PANEL_NONE || !!watchedContactId || !!watchedIsGuest
    onPanelChange?.(engaged)
    return () => onPanelChange?.(false)
  }, [picker.panel, watchedContactId, watchedIsGuest, onPanelChange])

  useEffect(() => {
    submitRef.current = async () => {
      const values = form.getValues()
      const hasSelection =
        !!values.contact_id || !!values.is_guest || picker.panel !== PANEL_NONE
      if (!hasSelection) return null

      const result = await picker.resolveSelection(values)
      if (result.isGuest) {
        setContact(GUEST_CONTACT)
      } else if (result.contact) {
        setContact(result.contact)
      }
      if (result.contactBoat) {
        setBoat(result.contactBoat)
      }
      return result
    }
    return () => {
      submitRef.current = null
    }
  }, [picker, form, submitRef, setContact, setBoat])

  return (
    <>
      <ContactBoatPicker
        handle={picker}
        enableGuest={enableGuest}
        guestLabel={GUEST_DISPLAY_NAME}
        extraFields={EXTRA_FIELDS}
      />
      {form.formState.errors?.root?.picker && (
        <p className="mt-2 text-sm text-red-600">
          {form.formState.errors.root.picker.message}
        </p>
      )}
    </>
  )
}

PickerInner.propTypes = {
  enableGuest: PropTypes.bool.isRequired,
  submitRef: PropTypes.object.isRequired,
  onPanelChange: PropTypes.func,
  setContact: PropTypes.func.isRequired,
  setBoat: PropTypes.func.isRequired,
}

const CustomerSelectionScreen = () => {
  const {
    flowDetails: {
      customerIdentityRequired,
      customerSelectionSubmitRef,
      setCustomerPanelActive,
    },
  } = useContext(PaymentFlowContext)
  const { contact, boat, setContact, setBoat } = useContext(POSContext)

  // Hydrate the picker once from whatever's currently on the cart so the
  // chip stays visible across Continue → Back. No live sync after mount;
  // changes only commit on submit.
  const form = useForm({
    defaultValues: {
      contact_id: contact?.isGuest ? "" : contact?.id ?? "",
      contact: contact?.isGuest || !contact?.id ? EMPTY_CONTACT : contact,
      contact_boat_id: boat?.id ?? "",
      contact_boat: boat?.id ? boat : EMPTY_CONTACT_BOAT,
      is_guest: !!contact?.isGuest,
    },
  })

  return (
    <div className="mb-4">
      <FormProvider {...form}>
        <PickerInner
          enableGuest={!customerIdentityRequired}
          submitRef={customerSelectionSubmitRef}
          onPanelChange={setCustomerPanelActive}
          setContact={setContact}
          setBoat={setBoat}
        />
      </FormProvider>
    </div>
  )
}

export default CustomerSelectionScreen
